/**
 * Email HTML validation — blocks content that breaks email delivery/rendering or
 * that ESPs reject. Runs on the final exported EDM HTML before save / test-send,
 * so users see the reason up-front instead of an opaque Newsleopard `40001`.
 *
 * Only `<script>` is empirically confirmed rejected (Newsleopard 40001, IKA-065083-1
 * wf 946); the others are blocked as XSS/phishing/rendering risks that mail clients
 * strip anyway. Newsleopard's full HTML spec is still pending their reply.
 *
 * These come from pasted custom HTML / custom-code blocks — unlayer's own output
 * does not emit them. (An `external_css` rule for `<link rel=stylesheet>` was
 * removed 2026-07: unlayer auto-injects a Google-Fonts stylesheet link for web
 * fonts — harmless, but it false-positived and blocked legit EDMs, e.g. 神坊 wf 705.
 * Same reason the `<head><style>` below is intentionally exempt.)
 */

// Newsleopard recommends keeping the HTML source under 102KB; above this Gmail
// truncates the message ("部分內容已遭到隱藏"), breaking layout + unsubscribe link.
export const EMAIL_HTML_MAX_BYTES = 102 * 1024

export type EmailHtmlIssueCode =
  | 'script'
  | 'iframe'
  | 'dangerous_tag'
  | 'form'
  | 'inline_js'
  | 'size'

const TAG_RULES: { code: EmailHtmlIssueCode; re: RegExp }[] = [
  { code: 'script', re: /<script[\s/>]/i },
  { code: 'iframe', re: /<iframe[\s/>]/i },
  { code: 'dangerous_tag', re: /<(object|embed|applet)[\s/>]/i },
  { code: 'form', re: /<form[\s/>]/i },
  // inline JS event handler attribute inside a tag, e.g. <a onclick="...">
  { code: 'inline_js', re: /<[^>]*\son[a-z]+\s*=/i },
]

/**
 * Returns the list of validation issues found in the given email HTML.
 * Empty array means the HTML is safe to send. Note: an HTML `<head><style>`
 * block is intentionally NOT flagged — unlayer always emits one for responsive
 * styles, so blocking it would reject every legitimate template.
 */
export const validateEmailHtml = (html: string): EmailHtmlIssueCode[] => {
  const codes: EmailHtmlIssueCode[] = []
  for (const rule of TAG_RULES) {
    if (rule.re.test(html)) {
      codes.push(rule.code)
    }
  }
  if (new TextEncoder().encode(html).length > EMAIL_HTML_MAX_BYTES) {
    codes.push('size')
  }
  return codes
}

/** Thrown by the email editor export flow when validateEmailHtml finds issues. */
export class EmailHtmlValidationError extends Error {
  codes: EmailHtmlIssueCode[]

  constructor(codes: EmailHtmlIssueCode[]) {
    super(`email html validation failed: ${codes.join(', ')}`)
    this.name = 'EmailHtmlValidationError'
    this.codes = codes
  }
}
